// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/app/components/layout/pioneer/InstallPioneerchatCard.module.css';
import {isInsidePioneerchatDesktop} from '@app/features/app/components/layout/pioneer/DesktopAppDetection';
import {
	canPromptInstall,
	promptInstall,
	subscribeInstallPrompt,
	wasJustInstalled,
} from '@app/features/app/components/layout/pioneer/InstallPromptCapture';
import {PioneerlandsMark} from '@app/features/app/components/layout/pioneer/PioneerlandsMark';
import * as NagbarCommands from '@app/features/ui/commands/NagbarCommands';
import NagbarState from '@app/features/ui/state/Nagbar';
import {isStandalonePwa} from '@app/features/ui/utils/PwaUtils';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {
	AppleLogoIcon,
	DotsThreeVerticalIcon,
	ExportIcon,
	PlusSquareIcon,
	WindowsLogoIcon,
	XIcon,
} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useEffect, useState, useSyncExternalStore} from 'react';

const TITLE_PHONE = msg({message: 'Install Pioneerchat', comment: 'Install card title on phones.'});
const TITLE_COMPUTER = msg({message: 'Get the Pioneerchat app', comment: 'Install card title on computers.'});
const LEDE_PHONE = msg({
	message: 'You only get notifications for new messages in the installed app, not in the browser.',
	comment: 'Install card explanation on phones.',
});
const LEDE_COMPUTER = msg({
	message: 'Its own window, notifications and an unread badge.',
	comment: 'Install card explanation on computers.',
});
const STEP_IOS_SHARE = msg({message: 'Tap Share', comment: 'iPhone install step 1, next to the Share icon.'});
const STEP_IOS_ADD = msg({
	message: 'Tap Add to Home Screen',
	comment: 'iPhone install step 2, next to the Add to Home Screen icon.',
});
const STEP_ANDROID_MENU = msg({message: 'Tap the menu', comment: 'Android install step 1, next to the ⋮ icon.'});
const STEP_ANDROID_INSTALL = msg({
	message: 'Tap Install app or Add to Home screen',
	comment: 'Android install step 2.',
});
const INSTALL = msg({message: 'Install', comment: 'Button that opens the browser install window.'});
const INSTALL_IN_BROWSER = msg({
	message: 'Install from this browser',
	comment: 'Secondary button that installs the web app from the browser on a computer.',
});
const DOWNLOAD_WINDOWS = msg({message: 'Download for Windows', comment: 'Install card desktop download.'});
const DOWNLOAD_MAC = msg({message: 'Download for Mac', comment: 'Install card desktop download.'});
const NOT_NOW = msg({message: 'Not now', comment: 'Hides the install card for good.'});
const CLOSE = msg({message: 'Close', comment: 'Close button label on the install card.'});

type Device = 'ios' | 'android' | 'windows' | 'mac' | 'other';

function detectDevice(): Device {
	const ua = navigator.userAgent;
	if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) return 'ios';
	if (/Android/i.test(ua)) return 'android';
	if (/Windows/i.test(ua)) return 'windows';
	if (/Macintosh|Mac OS X/i.test(ua)) return 'mac';
	return 'other';
}

function useCanPromptInstall(): boolean {
	return useSyncExternalStore(subscribeInstallPrompt, canPromptInstall, () => false);
}

export const InstallPioneerchatCard = observer(function InstallPioneerchatCard() {
	const {i18n} = useLingui();
	const canPrompt = useCanPromptInstall();
	const justInstalled = useSyncExternalStore(subscribeInstallPrompt, wasJustInstalled, () => false);
	// Wait a moment after the app opens, so the card doesn't land on top of the first paint.
	const [ready, setReady] = useState(false);
	useEffect(() => {
		const timer = window.setTimeout(() => setReady(true), 2500);
		return () => window.clearTimeout(timer);
	}, []);

	if (!ready || justInstalled) return null;
	if (NagbarState.pwaInstallDismissed) return null;
	if (isStandalonePwa() || isInsidePioneerchatDesktop()) return null;

	const device = detectDevice();
	const phone = device === 'ios' || device === 'android';
	const dismiss = () => NagbarCommands.dismissNagbar('pwaInstallDismissed');
	const install = async () => {
		if (await promptInstall()) dismiss();
	};

	const downloads = [
		{key: 'windows', href: '/download/Pioneerchat-windows.exe', icon: WindowsLogoIcon, label: DOWNLOAD_WINDOWS},
		{key: 'mac', href: '/download/Pioneerchat-mac.dmg', icon: AppleLogoIcon, label: DOWNLOAD_MAC},
	].sort((a, b) => Number(b.key === device) - Number(a.key === device));

	return (
		<section
			className={styles.card}
			aria-labelledby="install-pioneerchat-title"
			data-flx="app.pioneer.install-pioneerchat-card.card"
		>
			<div className={styles.head} data-flx="app.pioneer.install-pioneerchat-card.head">
				<span className={styles.mark} aria-hidden="true" data-flx="app.pioneer.install-pioneerchat-card.mark">
					<PioneerlandsMark className={styles.markIcon} />
				</span>
				<div data-flx="app.pioneer.install-pioneerchat-card.div">
					<h2 id="install-pioneerchat-title" className={styles.title} data-flx="app.pioneer.install-pioneerchat-card.title">
						{i18n._(phone ? TITLE_PHONE : TITLE_COMPUTER)}
					</h2>
					<p className={styles.lede} data-flx="app.pioneer.install-pioneerchat-card.lede">
						{i18n._(phone ? LEDE_PHONE : LEDE_COMPUTER)}
					</p>
				</div>
				<button
					type="button"
					className={styles.close}
					aria-label={i18n._(CLOSE)}
					onClick={dismiss}
					data-flx="app.pioneer.install-pioneerchat-card.close.dismiss.button"
				>
					<XIcon size={18} aria-hidden="true" data-flx="app.pioneer.install-pioneerchat-card.x-icon" />
				</button>
			</div>

			{device === 'ios' && (
				<ol className={styles.steps} data-flx="app.pioneer.install-pioneerchat-card.steps">
					<li className={styles.step} data-flx="app.pioneer.install-pioneerchat-card.step">
						<span className={styles.stepNumber} data-flx="app.pioneer.install-pioneerchat-card.step-number">
							1
						</span>
						<span data-flx="app.pioneer.install-pioneerchat-card.span">
							{i18n._(STEP_IOS_SHARE)}{' '}
							<span className={styles.stepIcon} data-flx="app.pioneer.install-pioneerchat-card.step-icon">
								<ExportIcon size={20} aria-hidden="true" data-flx="app.pioneer.install-pioneerchat-card.export-icon" />
							</span>
						</span>
					</li>
					<li className={styles.step} data-flx="app.pioneer.install-pioneerchat-card.step--2">
						<span className={styles.stepNumber} data-flx="app.pioneer.install-pioneerchat-card.step-number--2">
							2
						</span>
						<span data-flx="app.pioneer.install-pioneerchat-card.span--2">
							{i18n._(STEP_IOS_ADD)}{' '}
							<span className={styles.stepIcon} data-flx="app.pioneer.install-pioneerchat-card.step-icon--2">
								<PlusSquareIcon
									size={20}
									aria-hidden="true"
									data-flx="app.pioneer.install-pioneerchat-card.plus-square-icon"
								/>
							</span>
						</span>
					</li>
				</ol>
			)}

			{device === 'android' && !canPrompt && (
				<ol className={styles.steps} data-flx="app.pioneer.install-pioneerchat-card.steps--2">
					<li className={styles.step} data-flx="app.pioneer.install-pioneerchat-card.step--3">
						<span className={styles.stepNumber} data-flx="app.pioneer.install-pioneerchat-card.step-number--3">
							1
						</span>
						<span data-flx="app.pioneer.install-pioneerchat-card.span--3">
							{i18n._(STEP_ANDROID_MENU)}{' '}
							<span className={styles.stepIcon} data-flx="app.pioneer.install-pioneerchat-card.step-icon--3">
								<DotsThreeVerticalIcon
									size={20}
									weight="bold"
									aria-hidden="true"
									data-flx="app.pioneer.install-pioneerchat-card.dots-three-vertical-icon"
								/>
							</span>
						</span>
					</li>
					<li className={styles.step} data-flx="app.pioneer.install-pioneerchat-card.step--4">
						<span className={styles.stepNumber} data-flx="app.pioneer.install-pioneerchat-card.step-number--4">
							2
						</span>
						<span data-flx="app.pioneer.install-pioneerchat-card.span--4">{i18n._(STEP_ANDROID_INSTALL)}</span>
					</li>
				</ol>
			)}

			<div className={styles.actions} data-flx="app.pioneer.install-pioneerchat-card.actions">
				{phone && canPrompt && (
					<button
						type="button"
						className={styles.primary}
						onClick={install}
						data-flx="app.pioneer.install-pioneerchat-card.primary.install.button"
					>
						{i18n._(INSTALL)}
					</button>
				)}
				{!phone &&
					downloads.map((download, index) => {
						const Icon = download.icon;
						return (
							<a
								key={download.key}
								className={index === 0 ? styles.primary : styles.secondary}
								href={download.href}
								download
								onClick={dismiss}
								data-flx="app.pioneer.install-pioneerchat-card.download.dismiss"
							>
								<Icon size={18} aria-hidden="true" data-flx="app.pioneer.install-pioneerchat-card.icon" />
								{i18n._(download.label)}
							</a>
						);
					})}
				{!phone && canPrompt && (
					<button
						type="button"
						className={styles.secondary}
						onClick={install}
						data-flx="app.pioneer.install-pioneerchat-card.secondary.install.button"
					>
						{i18n._(INSTALL_IN_BROWSER)}
					</button>
				)}
				<button
					type="button"
					className={styles.later}
					onClick={dismiss}
					data-flx="app.pioneer.install-pioneerchat-card.later.dismiss.button"
				>
					{i18n._(NOT_NOW)}
				</button>
			</div>
		</section>
	);
});
